import React from "react";
import { z } from "zod";

import {
	FormCheckboxGroup,
	FormDateRangePicker,
	FormField,
	FormRadioButtons,
	PanelFilters,
} from "@performyard/py-components";
import type { DateRange } from "@performyard/py-components";
import { WORKFLOW_DETAIL_TODO_STATUSES } from "@performyard/service-contracts";
import type { WorkflowDetailTodoStatusFilter } from "@performyard/service-contracts";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";

import type { NamedReference } from "../todoFilterState";
import { NamedReferenceSchema } from "../todoFilterState";
import { DETAIL_TODO_STATUS_OPTIONS } from "./statuses";

export type WorkflowDetailsTodoFilters = {
	assignee?: NamedReference | null;
	status?: WorkflowDetailTodoStatusFilter;
	dueDateStart?: Date;
	dueDateEnd?: Date;
	hasSignatureRequest?: string[];
};

const DateRangeSchema = z.object({ from: z.date().optional(), to: z.date().optional() }).optional();

const WorkflowDetailsTodoFilterFormSchema = z.object({
	assignee: NamedReferenceSchema.nullable().default(null),
	status: z.enum(["any", ...WORKFLOW_DETAIL_TODO_STATUSES]).default("any"),
	dueDate: DateRangeSchema,
	hasSignatureRequest: z.array(z.string()).default([]),
});

type WorkflowDetailsTodoFilterForm = z.infer<typeof WorkflowDetailsTodoFilterFormSchema>;

const toDateRange = (start?: Date, end?: Date): DateRange | undefined =>
	start || end ? { from: start, to: end } : undefined;

type WorkflowDetailsFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<WorkflowDetailsTodoFilters>;
	onApplyFilters: (filters: Partial<WorkflowDetailsTodoFilters>) => void;
};

const WorkflowDetailsFilterPanel = ({ isOpen, onClose, filters, onApplyFilters }: WorkflowDetailsFilterPanelProps) => {
	const handleSubmit = ({ value }: { value: WorkflowDetailsTodoFilterForm }) => {
		const applied: Partial<WorkflowDetailsTodoFilters> = {};
		if (value.assignee) applied.assignee = value.assignee;
		if (value.status !== "any") applied.status = value.status;
		if (value.dueDate?.from) applied.dueDateStart = value.dueDate.from;
		if (value.dueDate?.to) applied.dueDateEnd = value.dueDate.to;
		if (value.hasSignatureRequest.length > 0) applied.hasSignatureRequest = ["true"];

		onApplyFilters(applied);
		onClose();
	};

	const defaultValues: WorkflowDetailsTodoFilterForm = {
		assignee: filters.assignee || null,
		status: filters.status || "any",
		dueDate: toDateRange(filters.dueDateStart, filters.dueDateEnd),
		hasSignatureRequest: filters.hasSignatureRequest || [],
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={WorkflowDetailsTodoFilterFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			headerWeight="bold"
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string } | null>
					id="assignee"
					label="Assignee"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value}
							onChange={(employeeId, employeeName) =>
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								)
							}
							placeholder="Make a selection"
							data-testid="assigneeFilter"
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={[...DETAIL_TODO_STATUS_OPTIONS]} />

				<FormDateRangePicker id="dueDate" label="Due Date Range" hasClearButton />

				<FormCheckboxGroup
					id="hasSignatureRequest"
					options={[{ label: "Has Signature Request", value: "true" }]}
				/>
			</div>
		</PanelFilters>
	);
};

export default WorkflowDetailsFilterPanel;
